import { Link, useSearchParams } from 'react-router-dom';
import { Tooltip } from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityHistoryFeed } from '../../../components/entity-detail/EntityHistoryFeed.js';
import { EntityTaskDueChip } from '../../../components/entity-detail/EntityTaskDueChip.js';
import {
  browserTimeZone,
  formatDateTime,
  formatMeetingRange,
} from '../../../shared/format/entity-detail.js';
import type { TaskItem } from '../../tasks/api/tasks.client.js';
import type { MeetingItem } from '../../meetings/api/meetings.client.js';
import type { DealHistoryItem } from '../hooks/useDealHistory.js';
import { DealNextContact } from './DealNextContact.js';

export function DealOverview({
  dealId,
  dueAt,
  tasks,
  meetings,
  history,
}: {
  dealId: string;
  dueAt: string | null;
  tasks: TaskItem[];
  meetings: MeetingItem[];
  history: DealHistoryItem[];
}) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const taskParams = new URLSearchParams(params);
  taskParams.set('tab', 'tasks');
  const stageParams = new URLSearchParams(params);
  stageParams.set('tab', 'history');
  stageParams.set('historyFilter', 'stages');
  const overdue = tasks
    .filter(
      (task) =>
        task.status !== 'done' &&
        task.status !== 'cancelled' &&
        task.dueAt &&
        Date.parse(task.dueAt) < Date.now(),
    )
    .sort((a, b) => Date.parse(a.dueAt!) - Date.parse(b.dueAt!));
  const nextMeeting = [...meetings]
    .filter(
      (meeting) => meeting.status === 'scheduled' && Date.parse(meeting.startsAt) >= Date.now(),
    )
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))[0];
  const stepOverdue = Boolean(dueAt && Date.parse(dueAt) < Date.now());
  const overdueCount = overdue.length + Number(stepOverdue);
  return (
    <div className="entity-overview">
      <section className="entity-panel">
        <h3>{t('crm.deal_detail.next_step')}</h3>
        <div className="entity-overview-action">
          <span className={stepOverdue ? 'deal-next-step-overdue' : undefined}>
            {dueAt ? formatDateTime(dueAt) : t('crm.deal_detail.not_planned')}
            {stepOverdue && dueAt ? (
              <span className="deal-overdue-step-label">
                {t('crm.deal_detail.step_overdue_since', {
                  date: formatDateTime(dueAt).slice(0, 10),
                })}
              </span>
            ) : null}
          </span>
          <DealNextContact dealId={dealId} dueAt={dueAt} />
        </div>
      </section>
      {overdueCount ? (
        <section className="entity-panel">
          <div className="entity-list-toolbar">
            <h3>{t('crm.entity_detail.overdue_tasks', { count: overdueCount })}</h3>
            <Link to={`?${taskParams.toString()}`}>{t('crm.entity_detail.all_tasks')}</Link>
          </div>
          {stepOverdue && dueAt ? (
            <div className="entity-overview-action">
              <span>{t('crm.deal_detail.next_step')}</span>
              <span className="deal-next-step-overdue">
                {t('crm.deal_detail.step_overdue_since', {
                  date: formatDateTime(dueAt).slice(0, 10),
                })}
              </span>
            </div>
          ) : null}
          {overdue.slice(0, stepOverdue ? 2 : 3).map((task) => (
            <div key={task.id} className="entity-overview-action">
              <Link to={`/tasks/${task.id}`}>{task.title}</Link>
              <EntityTaskDueChip dueAt={task.dueAt ?? null} />
            </div>
          ))}
        </section>
      ) : null}
      {nextMeeting ? (
        <section className="entity-panel">
          <h3>{t('crm.entity_detail.next_meeting')}</h3>
          <div className="entity-overview-action">
            <Link to={`/meetings/${nextMeeting.id}`}>{nextMeeting.title}</Link>
            <Tooltip
              title={
                nextMeeting.timezone !== browserTimeZone()
                  ? formatDateTime(nextMeeting.startsAt, nextMeeting.timezone)
                  : undefined
              }
            >
              <time dateTime={nextMeeting.startsAt}>
                {formatMeetingRange(nextMeeting.startsAt, nextMeeting.endsAt)}
              </time>
            </Tooltip>
          </div>
        </section>
      ) : null}
      <section className="entity-panel">
        <div className="entity-list-toolbar">
          <h3>{t('crm.entity_detail.recent_history')}</h3>
          <Link to={`?${stageParams.toString()}`}>{t('crm.deal_detail.all_stage_changes')}</Link>
        </div>
        <EntityHistoryFeed
          items={history.slice(0, 3).map((item) => ({
            id: item.id,
            date: item.date,
            text: item.text,
            meta: item.actor,
            icon: item.kind === 'deal_stage_changed' ? <SwapOutlined /> : undefined,
          }))}
        />
      </section>
    </div>
  );
}
